/**
 * VALLEYSTEALZ - Main Application & Interactive Orchestrator
 */

document.addEventListener('DOMContentLoaded', () => {
  // Initialize Scrollytelling Canvas
  const scroller = new ScrollyCanvasEngine({
    canvasId: 'hero-canvas',
    containerSelector: '.scrolly-container',
    totalFrames: 241
  });

  // UI Components
  initNavbarScroll();
  initAmbientAudio();
  initCountdownTimer();
  initSizeSelectors();
  initHotspotInteractions();
  initCartDrawer();
  initUnitToggle();
  initNewsletter();
  initMobileMenu();
  initViewSwitchers();
});

/* ==========================================================================
   1. NAVBAR SCROLL EFFECT
   ========================================================================== */
function initNavbarScroll() {
  const navbar = document.querySelector('.navbar-fixed');
  if (!navbar) return;

  const handleNavClass = () => {
    if (window.scrollY > 50) {
      navbar.classList.add('scrolled');
    } else {
      navbar.classList.remove('scrolled');
    }
  };

  // Active Scrollspy & Smooth scroll for navigation anchor links
  const navLinks = document.querySelectorAll('.nav-links a');
  const sectionIds = ['overview', 'statement-series', 'signature-series'];
  const sections = sectionIds
    .map(id => document.getElementById(id))
    .filter(Boolean);

  let currentNavId = '';
  const updateActiveNav = () => {
    const scrollPos = window.scrollY + 140;
    let currentId = 'overview';

    for (let i = 0; i < sections.length; i++) {
      const sec = sections[i];
      const top = sec.offsetTop;
      const height = sec.offsetHeight;
      if (scrollPos >= top && scrollPos < top + height) {
        currentId = sec.id;
        break;
      }
    }

    if ((window.innerHeight + window.scrollY) >= (document.documentElement.scrollHeight - 60)) {
      currentId = 'signature-series';
    }

    if (currentNavId !== currentId) {
      currentNavId = currentId;
      navLinks.forEach(link => {
        const href = link.getAttribute('href');
        if (href === '#' + currentId) {
          link.classList.add('active');
        } else {
          link.classList.remove('active');
        }
      });
    }
  };

  let navScrollTicking = false;
  window.addEventListener('scroll', () => {
    if (!navScrollTicking) {
      requestAnimationFrame(() => {
        handleNavClass();
        updateActiveNav();
        navScrollTicking = false;
      });
      navScrollTicking = true;
    }
  }, { passive: true });

  handleNavClass();
  updateActiveNav();

  document.querySelectorAll('a[href^="#"]').forEach(anchor => {
    anchor.addEventListener('click', function(e) {
      const targetId = this.getAttribute('href');
      if (!targetId || targetId === '#') return;
      const targetEl = document.querySelector(targetId);
      if (targetEl) {
        e.preventDefault();

        // Immediate visual active feedback for nav tabs
        if (this.closest('.nav-links')) {
          navLinks.forEach(l => l.classList.remove('active'));
          this.classList.add('active');
        }

        const navHeight = navbar.offsetHeight || 70;
        const targetTop = targetEl.getBoundingClientRect().top + window.scrollY - navHeight + 10;
        window.scrollTo({
          top: Math.max(0, targetTop),
          behavior: 'smooth'
        });
      }
    });
  });
}

/* ==========================================================================
   2. AMBIENT AUDIO SYNTHESIZER (Web Audio API Cyberpunk Drone)
   ========================================================================== */
function initAmbientAudio() {
  const audioBtn = document.getElementById('audio-toggle-btn');
  if (!audioBtn) return;

  let audioCtx = null;
  let isPlaying = false;
  let gainNode = null;
  let osc1 = null;
  let osc2 = null;
  let filter = null;

  function startAudio() {
    try {
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      audioCtx = new AudioContext();

      // Master Gain
      gainNode = audioCtx.createGain();
      gainNode.gain.setValueAtTime(0.001, audioCtx.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.08, audioCtx.currentTime + 3);

      // Low pass filter for dark, warm cinematic tone
      filter = audioCtx.createBiquadFilter();
      filter.type = 'lowpass';
      filter.frequency.setValueAtTime(140, audioCtx.currentTime);

      // Sub-bass drone oscillator (55Hz - A1)
      osc1 = audioCtx.createOscillator();
      osc1.type = 'sawtooth';
      osc1.frequency.setValueAtTime(55, audioCtx.currentTime);

      // Harmonic detuned 5th (82.5Hz - E2)
      osc2 = audioCtx.createOscillator();
      osc2.type = 'sine';
      osc2.frequency.setValueAtTime(82.5, audioCtx.currentTime);

      // Connect graph
      osc1.connect(filter);
      osc2.connect(filter);
      filter.connect(gainNode);
      gainNode.connect(audioCtx.destination);

      osc1.start();
      osc2.start();
      isPlaying = true;

      audioBtn.classList.add('audio-active');
      const lbl = audioBtn.querySelector('.audio-label');
      if (lbl) lbl.textContent = 'SOUND: ON';
      showToast('Cyberpunk Ambient Audio Enabled');
    } catch (e) {
      console.warn('Web Audio API not supported or blocked by user', e);
    }
  }

  function stopAudio() {
    if (gainNode && audioCtx) {
      gainNode.gain.exponentialRampToValueAtTime(0.0001, audioCtx.currentTime + 1);
      setTimeout(() => {
        if (osc1) osc1.stop();
        if (osc2) osc2.stop();
        if (audioCtx) audioCtx.close();
        audioCtx = null;
      }, 1000);
    }
    isPlaying = false;
    audioBtn.classList.remove('audio-active');
    const lbl = audioBtn.querySelector('.audio-label');
    if (lbl) lbl.textContent = 'SOUND: OFF';
  }

  audioBtn.addEventListener('click', () => {
    if (!isPlaying) {
      startAudio();
    } else {
      stopAudio();
    }
  });
}

/* ==========================================================================
   3. COUNTDOWN TIMER
   ========================================================================== */
function initCountdownTimer() {
  const daysEl = document.getElementById('count-days');
  const hoursEl = document.getElementById('count-hours');
  const minsEl = document.getElementById('count-mins');
  const secsEl = document.getElementById('count-secs');

  if (!daysEl || !hoursEl || !minsEl || !secsEl) return;

  // Set target date 2 days, 14 hours from now
  const targetDate = new Date(Date.now() + (2 * 24 + 14) * 60 * 60 * 1000);

  function updateTimer() {
    const now = new Date().getTime();
    const distance = targetDate.getTime() - now;

    if (distance < 0) {
      daysEl.textContent = '00';
      hoursEl.textContent = '00';
      minsEl.textContent = '00';
      secsEl.textContent = '00';
      return;
    }

    const days = Math.floor(distance / (1000 * 60 * 60 * 24));
    const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((distance % (1000 * 60)) / 1000);

    daysEl.textContent = String(days).padStart(2, '0');
    hoursEl.textContent = String(hours).padStart(2, '0');
    minsEl.textContent = String(minutes).padStart(2, '0');
    secsEl.textContent = String(seconds).padStart(2, '0');
  }

  updateTimer();
  setInterval(updateTimer, 1000);
}

/* ==========================================================================
   4. SIZE SELECTORS
   ========================================================================== */
function initSizeSelectors() {
  document.querySelectorAll('.size-selector-grid').forEach(grid => {
    const pills = grid.querySelectorAll('.size-pill');
    pills.forEach(pill => {
      pill.addEventListener('click', () => {
        pills.forEach(p => p.classList.remove('active'));
        pill.classList.add('active');
      });
    });
  });
}

/* ==========================================================================
   5. HOTSPOT INTERACTIONS
   ========================================================================== */
function initHotspotInteractions() {
  const hotspots = document.querySelectorAll('.hotspot-point');
  hotspots.forEach(hp => {
    hp.addEventListener('mouseenter', () => {
      hotspots.forEach(h => h.classList.remove('active'));
      hp.classList.add('active');
    });
  });
}

/* ==========================================================================
   6. CART STATE & DRAWER
   ========================================================================== */
function loadCart() {
  try {
    const saved = localStorage.getItem('valleystealz_cart_v1');
    if (saved) {
      const parsed = JSON.parse(saved);
      if (Array.isArray(parsed)) return parsed;
    }
  } catch (e) {
    console.warn('Could not read cart from localStorage', e);
  }
  return [];
}

function saveCart() {
  try {
    localStorage.setItem('valleystealz_cart_v1', JSON.stringify(cart));
  } catch (e) {
    console.warn('Could not save cart to localStorage', e);
  }
}

let cart = loadCart();

function initCartDrawer() {
  const cartTrigger = document.getElementById('cart-trigger-btn');
  const cartOverlay = document.getElementById('cart-drawer-overlay');
  const cartDrawer = document.getElementById('cart-drawer');
  const cartClose = document.getElementById('cart-close-btn');
  const checkoutBtn = document.getElementById('btn-checkout-now');

  function openCart() {
    renderCart();
    if (cartOverlay) cartOverlay.classList.add('open');
    if (cartDrawer) cartDrawer.classList.add('open');
  }

  function closeCart() {
    if (cartOverlay) cartOverlay.classList.remove('open');
    if (cartDrawer) cartDrawer.classList.remove('open');
  }

  if (cartTrigger) cartTrigger.addEventListener('click', openCart);
  if (cartClose) cartClose.addEventListener('click', closeCart);
  if (cartOverlay) {
    cartOverlay.addEventListener('click', (e) => {
      if (e.target === cartOverlay) closeCart();
    });
  }

  // Close with Escape key
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && cartDrawer && cartDrawer.classList.contains('open')) {
      closeCart();
    }
  });

  // Add to Bag buttons on collection cards
  document.querySelectorAll('.btn-add-bag').forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      const card = btn.closest('.product-card');
      if (!card) return;

      const title = card.querySelector('.product-title')?.textContent.trim() || 'VALLEYSTEALZ Tee';
      const priceStr = card.querySelector('.product-price')?.textContent || '$45';
      const price = parseInt(priceStr.replace(/[^0-9]/g, ''), 10) || 45;
      const activeSizePill = card.querySelector('.size-pill.active');
      const size = activeSizePill ? activeSizePill.textContent.trim() : 'L';
      const imgEl = card.querySelector('.product-img-wrapper img') || card.querySelector('img');
      const img = imgEl?.getAttribute('src') || imgEl?.src || 'assets/products/flagship_nokia_back.webp';

      const handle = card.getAttribute('data-handle') || '';
      let variantId = null;
      if (window.SHOPIFY_PRODUCTS_MAP && window.SHOPIFY_PRODUCTS_MAP[handle]) {
        const prod = window.SHOPIFY_PRODUCTS_MAP[handle];
        const matched = prod.variants.find(v => v.title.toLowerCase().trim() === size.toLowerCase().trim());
        if (matched) {
          variantId = matched.id;
        }
      }

      addToCart({
        id: `tee-${Date.now()}`,
        name: title,
        handle: handle,
        variantId: variantId,
        price: price,
        size: size,
        qty: 1,
        img: img
      });

      openCart();
    });
  });

  // Checkout handler - Real Shopify Cart & Payments Connection
  if (checkoutBtn) {
    checkoutBtn.addEventListener('click', () => {
      if (cart.length === 0) {
        alert('Your bag is currently empty.');
        return;
      }

      checkoutBtn.disabled = true;
      const originalText = checkoutBtn.textContent;
      checkoutBtn.textContent = 'CONNECTING TO CHECKOUT...';

      const items = cart.map(item => {
        let vId = item.variantId;
        if (!vId && item.handle && window.SHOPIFY_PRODUCTS_MAP && window.SHOPIFY_PRODUCTS_MAP[item.handle]) {
          const matched = window.SHOPIFY_PRODUCTS_MAP[item.handle].variants.find(v => v.title.toLowerCase().trim() === item.size.toLowerCase().trim());
          if (matched) vId = matched.id;
        }
        return {
          id: vId || (window.SHOPIFY_DEFAULT_VARIANT_ID || 61638188007564),
          quantity: item.qty,
          properties: {
            'Graphic': item.name,
            'Size': item.size
          }
        };
      });

      // Synchronize with Shopify Cart AJAX API and redirect to real Shopify /checkout
      fetch('/cart/clear.js', { method: 'POST' })
        .then(() => {
          return fetch('/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            },
            body: JSON.stringify({ items: items })
          });
        })
        .then(async (res) => {
          if (!res.ok) {
            const errData = await res.json().catch(() => ({}));
            throw errData;
          }
          return res.json();
        })
        .then(() => {
          // Direct to Shopify's secure payment checkout page
          window.location.href = '/checkout';
        })
        .catch(err => {
          console.error('Checkout error:', err);
          const errorMsg = err?.description || err?.message || 'Could not connect to Shopify Checkout.';
          alert('Checkout Notice:\n\n' + errorMsg + '\n\nTip: In Shopify Admin, please ensure the product inventory is marked Available or "Continue selling when out of stock" is enabled.');
          checkoutBtn.disabled = false;
          checkoutBtn.textContent = originalText;
        });
    });
  }

  // Initial render on page load
  renderCart();
}

function addToCart(item) {
  const existing = cart.find(x => x.name === item.name && x.size === item.size);
  if (existing) {
    existing.qty += item.qty;
  } else {
    cart.push(item);
  }
  saveCart();
  renderCart();
}

function renderCart() {
  const container = document.getElementById('cart-items-list');
  const navBadge = document.getElementById('cart-badge-count');
  const drawerBadge = document.getElementById('cart-badge-count-drawer');
  const subtotalEl = document.getElementById('cart-subtotal-price');
  const checkoutBtn = document.getElementById('btn-checkout-now');

  if (!container) return;

  const totalQty = cart.reduce((sum, item) => sum + item.qty, 0);
  const totalPrice = cart.reduce((sum, item) => sum + (item.price * item.qty), 0);

  // Synchronize Navbar Badge
  if (navBadge) {
    navBadge.textContent = totalQty;
    navBadge.style.display = totalQty > 0 ? 'flex' : 'none';
  }

  // Synchronize Drawer Title Badge
  if (drawerBadge) {
    drawerBadge.textContent = totalQty;
  }

  // Synchronize Subtotal
  if (subtotalEl) {
    subtotalEl.textContent = `$${totalPrice}.00`;
  }

  // Synchronize Checkout Button state
  if (checkoutBtn) {
    checkoutBtn.style.opacity = totalQty === 0 ? '0.5' : '1';
    checkoutBtn.style.cursor = totalQty === 0 ? 'not-allowed' : 'pointer';
  }

  // Render Cart Items or Empty State
  if (cart.length === 0) {
    container.innerHTML = `
      <div style="text-align: center; padding: 4rem 1.5rem; color: var(--text-muted);">
        <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" style="margin-bottom: 1rem; opacity: 0.35;">
          <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"></path>
          <line x1="3" y1="6" x2="21" y2="6"></line>
          <path d="M16 10a4 4 0 0 1-8 0"></path>
        </svg>
        <p style="margin-bottom: 0.4rem; font-size: 1.05rem; font-weight: 700; color: #fff;">Your bag is empty</p>
        <p style="font-size: 0.82rem; line-height: 1.5;">Explore the collection above and select your size.</p>
      </div>
    `;
    return;
  }

  container.innerHTML = cart.map((item, index) => `
    <div class="cart-item">
      <img src="${item.img}" alt="${item.name}" class="cart-item-img" />
      <div class="cart-item-info">
        <div style="display: flex; justify-content: space-between; align-items: flex-start; gap: 8px;">
          <div>
            <div class="cart-item-title">${item.name}</div>
            <div class="cart-item-meta">SIZE: ${item.size} &bull; HEAVYWEIGHT BLACK</div>
          </div>
          <button class="cart-remove-item" type="button" onclick="removeItem(${index})" title="Remove item" aria-label="Remove item">&times;</button>
        </div>
        <div class="cart-item-bottom">
          <div class="cart-item-price">$${item.price * item.qty}</div>
          <div class="cart-qty-ctrl">
            <button class="qty-btn" type="button" onclick="updateItemQty(${index}, -1)" aria-label="Decrease quantity">&minus;</button>
            <span style="font-size: 0.85rem; font-weight: 600; padding: 0 5px;">${item.qty}</span>
            <button class="qty-btn" type="button" onclick="updateItemQty(${index}, 1)" aria-label="Increase quantity">&plus;</button>
          </div>
        </div>
      </div>
    </div>
  `).join('');
}

window.updateItemQty = function(index, change) {
  if (!cart[index]) return;
  cart[index].qty += change;
  if (cart[index].qty <= 0) {
    cart.splice(index, 1);
  }
  saveCart();
  renderCart();
};

window.removeItem = function(index) {
  if (!cart[index]) return;
  cart.splice(index, 1);
  saveCart();
  renderCart();
};

/* ==========================================================================
   7. UNIT TOGGLE (Inches vs CM)
   ========================================================================== */
function initUnitToggle() {
  const inBtn = document.getElementById('unit-btn-in');
  const cmBtn = document.getElementById('unit-btn-cm');
  if (!inBtn || !cmBtn) return;

  const inData = [
    { size: 'S', chest: '44"', length: '29"', shoulder: '21"', sleeve: '9.0"' },
    { size: 'M', chest: '47"', length: '30"', shoulder: '22"', sleeve: '9.5"' },
    { size: 'L', chest: '50"', length: '31"', shoulder: '23"', sleeve: '10.0"' },
    { size: 'XL', chest: '53"', length: '32"', shoulder: '24"', sleeve: '10.5"' },
    { size: 'XXL', chest: '56"', length: '33"', shoulder: '25"', sleeve: '11.0"' }
  ];

  const cmData = [
    { size: 'S', chest: '112 cm', length: '74 cm', shoulder: '53 cm', sleeve: '23 cm' },
    { size: 'M', chest: '119 cm', length: '76 cm', shoulder: '56 cm', sleeve: '24 cm' },
    { size: 'L', chest: '127 cm', length: '79 cm', shoulder: '58 cm', sleeve: '25 cm' },
    { size: 'XL', chest: '135 cm', length: '81 cm', shoulder: '61 cm', sleeve: '27 cm' },
    { size: 'XXL', chest: '142 cm', length: '84 cm', shoulder: '64 cm', sleeve: '28 cm' }
  ];

  function renderTable(data) {
    const tbody = document.getElementById('sizing-table-body');
    if (!tbody) return;
    tbody.innerHTML = data.map(row => `
      <tr>
        <td style="font-weight: 700; color: #fff;">${row.size}</td>
        <td>${row.chest}</td>
        <td>${row.length}</td>
        <td>${row.shoulder}</td>
        <td>${row.sleeve}</td>
      </tr>
    `).join('');
  }

  inBtn.addEventListener('click', () => {
    inBtn.classList.add('active');
    cmBtn.classList.remove('active');
    renderTable(inData);
  });

  cmBtn.addEventListener('click', () => {
    cmBtn.classList.add('active');
    inBtn.classList.remove('active');
    renderTable(cmData);
  });

  renderTable(inData);
}

/* ==========================================================================
   8. NEWSLETTER VIP SIGNUP
   ========================================================================== */
function initNewsletter() {
  const form = document.getElementById('vip-newsletter-form');
  if (!form) return;

  form.addEventListener('submit', (e) => {
    e.preventDefault();
    const input = form.querySelector('input');
    if (input && input.value) {
      showToast('VIP Early Access Code Reserved: ' + input.value);
      input.value = '';
    }
  });
}

/* ==========================================================================
   9. MOBILE MENU
   ========================================================================== */
function initMobileMenu() {
  const toggle = document.getElementById('mobile-menu-toggle');
  const links = document.getElementById('nav-links');
  if (!toggle || !links) return;

  toggle.addEventListener('click', () => {
    links.classList.toggle('open');
    if (links.classList.contains('open')) {
      links.style.display = 'flex';
      links.style.flexDirection = 'column';
      links.style.position = 'absolute';
      links.style.top = '100%';
      links.style.left = '0';
      links.style.width = '100%';
      links.style.background = 'rgba(5, 5, 5, 0.95)';
      links.style.padding = '2rem';
      links.style.backdropFilter = 'blur(20px)';
      links.style.borderBottom = '1px solid var(--border-glass)';
    } else {
      links.style.display = '';
    }
  });
}

/* ==========================================================================
   10. TOAST NOTIFICATION
   ========================================================================== */
function showToast(msg) {
  let toast = document.getElementById('toast-msg');
  if (!toast) {
    toast = document.createElement('div');
    toast.id = 'toast-msg';
    toast.className = 'toast-msg';
    document.body.appendChild(toast);
  }
  toast.innerHTML = `
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#00D6FF" stroke-width="2">
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
      <polyline points="22 4 12 14.01 9 11.01"></polyline>
    </svg>
    <span>${msg}</span>
  `;
  toast.classList.add('show');
  setTimeout(() => {
    toast.classList.remove('show');
  }, 3500);
}

/* ==========================================================================
   11. FRONT / BACK VIEW SWITCHER
   ========================================================================== */
function initViewSwitchers() {
  document.querySelectorAll('.view-switcher-group').forEach(group => {
    const card = group.closest('.product-card');
    const img = card ? card.querySelector('.product-img-wrapper img') : null;
    const btns = group.querySelectorAll('.view-btn');

    btns.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        btns.forEach(b => b.classList.remove('active'));
        btn.classList.add('active');
        const targetSrc = btn.getAttribute('data-img');
        if (targetSrc && img) {
          img.style.opacity = '0.3';
          img.src = targetSrc;
          setTimeout(() => {
            img.style.opacity = '1';
          }, 120);
        }
      });
    });
  });
}


